import PropTypes from 'prop-types';
import { extend, useDataModel } from '@thd-nucleus/data-sources';
import { useHelmet } from '@thd-nucleus/thd-helmet';
import { EMT_PAGE_NAMES, LAST_PATH, OPEN_GRAPH_FACEBOOK_ADMIN } from '../../constants';
import { filterEmtMetadataOverrides } from '../../helpers/emt-content-helpers';
import { generateStorePageCanonical, generateStorePageTitle } from '../../helpers/pps-helpers';
import { emtContentModel, storeSearchModel } from '../../models';
import { generateTags } from '../../utils/generate-tags';
import {
  generateGardenCenterDesc,
  getGardenCenterPreloadImages,
  getGardenCenterSchemas
} from './garden-center-helpers';

// --- Tag Handler ---

const gardenCenterHandler = ({ data: { emtData, storeData }, props: { storeId } }) => {
  // Extract store details data once
  const storeInfo = {
    ...storeData?.storeSearch?.stores?.[0],
    storeId
  };
  const {
    name: storeName,
    address: { city, state, postalCode } = {},
    storeDetailsPageLink
  } = storeInfo;

  const canonicalURL = generateStorePageCanonical({
    storeDetailsPageLink, urlSuffix: LAST_PATH.GARDEN_CENTER
  });

  // Extract emtContent data once
  const emtContent = emtData?.emtContent?.content || {};
  const emtContentMetadata = emtContent?.gardenCenterMetadata || {};
  const storeFinderPromotions = emtContent?.storeFinderPromotions || {};

  const fallbackDescription = generateGardenCenterDesc({ storeName });
  const fallbackTitle = generateStorePageTitle({
    department: 'Garden Center',
    storeName,
    city,
    state,
    postalCode
  });
  const {
    metaDescription = fallbackDescription,
    ogDescription = fallbackDescription,
    ogImage,
    ogTitle = fallbackTitle,
    ogType = 'PPS Garden Center',
    schema: emtSchema,
    title = fallbackTitle
  } = filterEmtMetadataOverrides(emtContentMetadata);

  const preloadImages = getGardenCenterPreloadImages(storeFinderPromotions);

  const schemas = getGardenCenterSchemas({ emtSchema, storeInfo });

  // Generate Metadata Tags
  const tags = generateTags({
    canonical: canonicalURL,
    description: metaDescription,
    fbAdmins: OPEN_GRAPH_FACEBOOK_ADMIN,
    ogDescription,
    ogImage,
    ogSiteName: 'The Home Depot',
    ogTitle,
    ogType,
    ogUrl: canonicalURL,
    preloadImages,
    schemas,
    title
  });

  return tags;
};

// --- Main Component ---

const GardenCenterMetadata = ({ storeId }) => {
  const emtContentOptions = {
    variables: {
      contentId: storeId,
      pageName: EMT_PAGE_NAMES.GARDEN_CENTER
    }
  };
  const storeSearchOptions = {
    variables: {
      storeSearchInput: storeId,
      pagesize: '1'
    }
  };

  const { data: emtData } = useDataModel('emtContent', emtContentOptions);
  const { data: storeData } = useDataModel('storeSearch', storeSearchOptions);

  const data = { emtData, storeData };
  const props = { storeId };

  useHelmet('gardenCenterMetadata', { data, props, skipCleanup: true }, gardenCenterHandler, [storeId]);

  return null;
};

// --- Component Static Properties (dataModel, propTypes, etc.) ---

GardenCenterMetadata.displayName = 'GardenCenterMetadata';

GardenCenterMetadata.dataModel = extend(
  emtContentModel,
  storeSearchModel
);

GardenCenterMetadata.propTypes = {
  storeId: PropTypes.string.isRequired
};

export { GardenCenterMetadata };